(() => {
  // src/config.js
  var required = (value, label) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) throw new Error(`\u30DE\u30A4\u30DA\u30FC\u30B8\u306E${label}\u304C\u8A2D\u5B9A\u3055\u308C\u3066\u3044\u307E\u305B\u3093\u3002`);
    return normalized;
  };
  var requiredHttpsUrl = (value, label) => {
    const normalized = required(value, label);
    let parsed;
    try {
      parsed = new URL(normalized);
    } catch {
      throw new Error(`${label}\u306F\u6709\u52B9\u306AURL\u3067\u8A2D\u5B9A\u3057\u3066\u304F\u3060\u3055\u3044\u3002`);
    }
    if (parsed.protocol !== "https:") {
      throw new Error(`${label}\u306FHTTPS\u3067\u8A2D\u5B9A\u3057\u3066\u304F\u3060\u3055\u3044\u3002`);
    }
    return normalized;
  };
  function readPageConfig(root) {
    const redirectUri = requiredHttpsUrl(root.dataset.redirectUri, "redirect URI");
    const tokenExchangeUrl = requiredHttpsUrl(
      root.dataset.tokenExchangeUrl,
      "token exchange URL"
    );
    const jwksUrl = new URL("/oauth/jwks", tokenExchangeUrl).toString();
    return {
      clientId: required(root.dataset.clientId, "client ID"),
      storefrontDomain: required(root.dataset.storefrontDomain, "storefront domain"),
      redirectUri,
      tokenExchangeUrl,
      jwksUrl,
      locale: String(root.dataset.locale || "ja"),
      standardAccountUrl: required(root.dataset.standardAccountUrl, "\u6A19\u6E96\u30A2\u30AB\u30A6\u30F3\u30C8URL"),
      contactUrl: required(root.dataset.contactUrl, "\u304A\u554F\u3044\u5408\u308F\u305BURL"),
      shopUrl: required(root.dataset.shopUrl, "\u30B7\u30E7\u30C3\u30D7URL")
    };
  }

  // src/auth.js
  var base64UrlEncode = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  var AUTH_KEYS = {
    verifier: "ageta.auth.verifier",
    state: "ageta.auth.state",
    nonce: "ageta.auth.nonce",
    returnHash: "ageta.auth.returnHash",
    token: "ageta.auth.token",
    expiresAt: "ageta.auth.expiresAt",
    idToken: "ageta.auth.idToken",
    retried: "ageta.auth.retried"
  };
  var base64UrlDecode = (value) => {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  };
  var decodeJsonPart = (value) => JSON.parse(
    new TextDecoder().decode(base64UrlDecode(value))
  );
  var AuthError = class extends Error {
    constructor(message) {
      super(message);
      this.name = "AuthError";
    }
  };
  var AuthRedirectError = class extends Error {
    constructor() {
      super("Shopify\u8A8D\u8A3C\u3078\u79FB\u52D5\u3057\u307E\u3059\u3002");
      this.name = "AuthRedirectError";
    }
  };
  var createRandomToken = (cryptoImpl) => {
    const bytes = new Uint8Array(24);
    cryptoImpl.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  };
  async function createPkce(cryptoImpl = crypto) {
    const random = new Uint8Array(32);
    cryptoImpl.getRandomValues(random);
    const verifier = base64UrlEncode(random);
    const digest = await cryptoImpl.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(verifier)
    );
    return {
      verifier,
      challenge: base64UrlEncode(new Uint8Array(digest))
    };
  }
  async function discoverCustomerAccount(config, fetchImpl = fetch) {
    const origin = `https://${config.storefrontDomain}`;
    const [openidResponse, apiResponse] = await Promise.all([
      fetchImpl(`${origin}/.well-known/openid-configuration`),
      fetchImpl(`${origin}/.well-known/customer-account-api`)
    ]);
    if (!openidResponse.ok || !apiResponse.ok) {
      throw new AuthError("Shopify\u8A8D\u8A3C\u8A2D\u5B9A\u3092\u53D6\u5F97\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    }
    return {
      openid: await openidResponse.json(),
      api: await apiResponse.json()
    };
  }
  function createAuthClient({
    config,
    fetchImpl = fetch,
    cryptoImpl = crypto,
    storage = sessionStorage,
    location = window.location,
    history = window.history,
    now = Date.now
  }) {
    let discoveryPromise;
    const discover = () => {
      discoveryPromise || (discoveryPromise = discoverCustomerAccount(config, fetchImpl));
      return discoveryPromise;
    };
    const clear = () => {
      for (const key of Object.values(AUTH_KEYS)) storage.removeItem(key);
    };
    const clearTransient = () => {
      for (const key of [AUTH_KEYS.verifier, AUTH_KEYS.state, AUTH_KEYS.nonce, AUTH_KEYS.returnHash]) {
        storage.removeItem(key);
      }
    };
    const clearCredentials = () => {
      for (const key of [AUTH_KEYS.token, AUTH_KEYS.expiresAt, AUTH_KEYS.idToken]) {
        storage.removeItem(key);
      }
      clearTransient();
    };
    const processCallback = async (url) => {
      const code = url.searchParams.get("code");
      const returnedState = url.searchParams.get("state");
      const expectedState = storage.getItem(AUTH_KEYS.state);
      const verifier = storage.getItem(AUTH_KEYS.verifier);
      const nonce = storage.getItem(AUTH_KEYS.nonce);
      const returnHash = storage.getItem(AUTH_KEYS.returnHash) || "#home";
      if (!code || !returnedState || returnedState !== expectedState || !verifier || !nonce) {
        clear();
        throw new AuthError("\u8A8D\u8A3C\u60C5\u5831\u3092\u78BA\u8A8D\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002\u3082\u3046\u4E00\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002");
      }
      const { openid } = await discover();
      const body = new URLSearchParams({
        code,
        code_verifier: verifier
      });
      const tokenResponse = await fetchImpl(config.tokenExchangeUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body
      });
      if (!tokenResponse.ok) {
        clear();
        throw new AuthError("Shopify\u306E\u8A8D\u8A3C\u3092\u5B8C\u4E86\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
      }
      const tokenPayload = await tokenResponse.json();
      const expiresIn = Number(tokenPayload.expires_in);
      if (!tokenPayload.access_token || !tokenPayload.id_token || !Number.isFinite(expiresIn)) {
        clear();
        throw new AuthError("Shopify\u306E\u8A8D\u8A3C\u5FDC\u7B54\u3092\u78BA\u8A8D\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
      }
      const jwksResponse = await fetchImpl(config.jwksUrl);
      if (!jwksResponse.ok) {
        clear();
        throw new AuthError("Shopify\u306E\u7F72\u540D\u60C5\u5831\u3092\u53D6\u5F97\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
      }
      await verifyIdToken({
        token: tokenPayload.id_token,
        jwks: await jwksResponse.json(),
        issuer: openid.issuer,
        clientId: config.clientId,
        nonce,
        nowSeconds: Math.floor(now() / 1e3),
        cryptoImpl
      });
      storage.setItem(AUTH_KEYS.token, tokenPayload.access_token);
      storage.setItem(AUTH_KEYS.expiresAt, now() + expiresIn * 1e3 - 3e4);
      storage.setItem(AUTH_KEYS.idToken, tokenPayload.id_token);
      clearTransient();
      for (const parameter of ["code", "state", "error", "error_description"]) {
        url.searchParams.delete(parameter);
      }
      const search = url.searchParams.toString();
      history.replaceState(
        null,
        "",
        `${url.pathname}${search ? `?${search}` : ""}${returnHash}`
      );
      return tokenPayload.access_token;
    };
    const startAuthorization = async ({ prompt } = {}) => {
      const { openid } = await discover();
      const { verifier, challenge } = await createPkce(cryptoImpl);
      const state = createRandomToken(cryptoImpl);
      const nonce = createRandomToken(cryptoImpl);
      const currentUrl = new URL(location.href);
      storage.setItem(AUTH_KEYS.verifier, verifier);
      storage.setItem(AUTH_KEYS.state, state);
      storage.setItem(AUTH_KEYS.nonce, nonce);
      storage.setItem(AUTH_KEYS.returnHash, currentUrl.hash || "#home");
      const authorizationUrl = new URL(openid.authorization_endpoint);
      authorizationUrl.searchParams.set("scope", "openid email customer-account-api:full");
      authorizationUrl.searchParams.set("client_id", config.clientId);
      authorizationUrl.searchParams.set("response_type", "code");
      authorizationUrl.searchParams.set("redirect_uri", config.redirectUri);
      authorizationUrl.searchParams.set("state", state);
      authorizationUrl.searchParams.set("nonce", nonce);
      authorizationUrl.searchParams.set("code_challenge", challenge);
      authorizationUrl.searchParams.set("code_challenge_method", "S256");
      authorizationUrl.searchParams.set("locale", config.locale || "ja");
      if (prompt) authorizationUrl.searchParams.set("prompt", prompt);
      location.assign(authorizationUrl.toString());
      throw new AuthRedirectError();
    };
    const getAccessToken = async () => {
      const currentUrl = new URL(location.href);
      if (currentUrl.searchParams.has("code") || currentUrl.searchParams.has("state")) {
        return processCallback(currentUrl);
      }
      const accessToken = storage.getItem(AUTH_KEYS.token);
      const expiresAt = Number(storage.getItem(AUTH_KEYS.expiresAt));
      if (accessToken && Number.isFinite(expiresAt) && expiresAt > now()) return accessToken;
      return startAuthorization();
    };
    const reauthenticateOnce = async () => {
      if (storage.getItem(AUTH_KEYS.retried) === "true") {
        throw new AuthError("\u8A8D\u8A3C\u3092\u66F4\u65B0\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002\u6A19\u6E96\u30DE\u30A4\u30DA\u30FC\u30B8\u3092\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002");
      }
      clearCredentials();
      storage.setItem(AUTH_KEYS.retried, "true");
      return startAuthorization();
    };
    const logout = async () => {
      const idToken = storage.getItem(AUTH_KEYS.idToken);
      const { openid } = await discover();
      clear();
      const logoutUrl = new URL(openid.end_session_endpoint);
      if (idToken) logoutUrl.searchParams.set("id_token_hint", idToken);
      logoutUrl.searchParams.set("post_logout_redirect_uri", config.redirectUri);
      location.assign(logoutUrl.toString());
      throw new AuthRedirectError();
    };
    return {
      clear,
      getApiEndpoint: async () => {
        const { api } = await discover();
        if (!api.graphql_api) throw new AuthError("Customer Account API\u306E\u63A5\u7D9A\u5148\u3092\u53D6\u5F97\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
        return api.graphql_api;
      },
      getAccessToken,
      logout,
      markApiSuccess: () => storage.removeItem(AUTH_KEYS.retried),
      reauthenticateOnce
    };
  }
  async function verifyIdToken({
    token,
    jwks,
    issuer,
    clientId,
    nonce,
    nowSeconds,
    cryptoImpl = crypto
  }) {
    try {
      const parts = String(token).split(".");
      if (parts.length !== 3) throw new Error("malformed token");
      const [encodedHeader, encodedPayload, encodedSignature] = parts;
      const header = decodeJsonPart(encodedHeader);
      const payload = decodeJsonPart(encodedPayload);
      if (header.alg !== "RS256" || !header.kid) throw new Error("unsupported algorithm");
      const jwk = jwks?.keys?.find((candidate) => candidate.kid === header.kid);
      if (!jwk) throw new Error("unknown signing key");
      const key = await cryptoImpl.subtle.importKey(
        "jwk",
        jwk,
        { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
        false,
        ["verify"]
      );
      const signatureValid = await cryptoImpl.subtle.verify(
        "RSASSA-PKCS1-v1_5",
        key,
        base64UrlDecode(encodedSignature),
        new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`)
      );
      if (!signatureValid) throw new Error("invalid signature");
      const audienceValid = Array.isArray(payload.aud) ? payload.aud.includes(clientId) : payload.aud === clientId;
      if (payload.iss !== issuer || !audienceValid || payload.nonce !== nonce || !Number.isFinite(payload.exp) || payload.exp <= nowSeconds) {
        throw new Error("invalid claims");
      }
      return payload;
    } catch (error) {
      if (error instanceof AuthError) throw error;
      throw new AuthError("ID token\u3092\u691C\u8A3C\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    }
  }

  // src/customer-account-api.js
  var MONEY_FIELDS = "amount currencyCode";
  var ADDRESS_FIELDS = `
  id firstName lastName company address1 address2 city zoneCode territoryCode zip phoneNumber formatted
`;
  var ORDER_SUMMARY_FIELDS = `
  id name processedAt financialStatus fulfillmentStatus statusPageUrl
  totalPrice { ${MONEY_FIELDS} }
  lineItems(first: 10) { nodes { id name quantity } }
`;
  var ORDER_LINE_ITEM_FIELDS = `
  id name quantity
  image { url altText }
  totalPrice { ${MONEY_FIELDS} }
  totalDiscount { ${MONEY_FIELDS} }
`;
  var DASHBOARD_QUERY = `
  query AccountDashboard {
    customer {
      id firstName lastName displayName
      emailAddress { emailAddress }
      defaultAddress { ${ADDRESS_FIELDS} }
      orders(first: 3, reverse: true) { nodes { ${ORDER_SUMMARY_FIELDS} } }
    }
  }
`;
  var ORDERS_QUERY = `
  query AccountOrders($after: String) {
    customer {
      orders(first: 20, after: $after, reverse: true) {
        nodes { ${ORDER_SUMMARY_FIELDS} }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;
  var ORDER_QUERY = `
  query AccountOrder($orderId: ID!) {
    order(id: $orderId) {
      id name processedAt financialStatus fulfillmentStatus statusPageUrl
      subtotal { ${MONEY_FIELDS} }
      totalShipping { ${MONEY_FIELDS} }
      totalTax { ${MONEY_FIELDS} }
      totalPrice { ${MONEY_FIELDS} }
      shippingDiscountAllocations { allocatedAmount { ${MONEY_FIELDS} } }
      shippingAddress { ${ADDRESS_FIELDS} }
      lineItems(first: 100) {
        nodes { ${ORDER_LINE_ITEM_FIELDS} }
        pageInfo { hasNextPage endCursor }
      }
      fulfillments(first: 20) {
        nodes {
          id status estimatedDeliveryAt
          trackingInformation { number url }
        }
      }
    }
  }
`;
  var ORDER_LINE_ITEMS_QUERY = `
  query AccountOrderLineItems($orderId: ID!, $after: String!) {
    order(id: $orderId) {
      lineItems(first: 100, after: $after) {
        nodes { ${ORDER_LINE_ITEM_FIELDS} }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;
  var PROFILE_QUERY = `
  query AccountProfile {
    customer { id firstName lastName displayName emailAddress { emailAddress } }
  }
`;
  var ADDRESSES_QUERY = `
  query AccountAddresses {
    customer {
      defaultAddress { ${ADDRESS_FIELDS} }
      addresses(first: 100) { nodes { ${ADDRESS_FIELDS} } }
    }
  }
`;
  var UPDATE_PROFILE_MUTATION = `
  mutation UpdateProfile($input: CustomerUpdateInput!) {
    customerUpdate(input: $input) {
      customer { id firstName lastName displayName emailAddress { emailAddress } }
      userErrors { field message }
    }
  }
`;
  var CREATE_ADDRESS_MUTATION = `
  mutation CreateAddress($address: CustomerAddressInput!, $defaultAddress: Boolean) {
    customerAddressCreate(address: $address, defaultAddress: $defaultAddress) {
      customerAddress { ${ADDRESS_FIELDS} }
      userErrors { field message }
    }
  }
`;
  var UPDATE_ADDRESS_MUTATION = `
  mutation UpdateAddress($address: CustomerAddressInput, $addressId: ID!, $defaultAddress: Boolean) {
    customerAddressUpdate(address: $address, addressId: $addressId, defaultAddress: $defaultAddress) {
      customerAddress { ${ADDRESS_FIELDS} }
      userErrors { field message }
    }
  }
`;
  var DELETE_ADDRESS_MUTATION = `
  mutation DeleteAddress($addressId: ID!) {
    customerAddressDelete(addressId: $addressId) {
      deletedAddressId
      userErrors { field message }
    }
  }
`;
  var CustomerAccountApiError = class extends Error {
    constructor(kind, message, options = {}) {
      super(message);
      this.name = "CustomerAccountApiError";
      this.kind = kind;
      this.status = options.status ?? null;
      this.fieldErrors = options.fieldErrors ?? [];
    }
  };
  var mutationResult = (payload, valueKey) => {
    const fieldErrors = payload?.userErrors ?? [];
    if (fieldErrors.length > 0) {
      throw new CustomerAccountApiError(
        "validation",
        "\u5165\u529B\u5185\u5BB9\u3092\u78BA\u8A8D\u3057\u3066\u304F\u3060\u3055\u3044\u3002",
        { fieldErrors }
      );
    }
    return payload?.[valueKey] ?? null;
  };
  function createCustomerAccountClient({
    endpoint,
    accessToken,
    fetchImpl = fetch
  }) {
    const request = async (query, variables = {}) => {
      let response;
      try {
        response = await fetchImpl(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken
          },
          body: JSON.stringify({ query, variables })
        });
      } catch {
        throw new CustomerAccountApiError("network", "\u901A\u4FE1\u306B\u5931\u6557\u3057\u307E\u3057\u305F\u3002");
      }
      if (response.status === 401) {
        throw new CustomerAccountApiError(
          "unauthorized",
          "\u8A8D\u8A3C\u306E\u6709\u52B9\u671F\u9650\u304C\u5207\u308C\u307E\u3057\u305F\u3002",
          { status: 401 }
        );
      }
      if (response.status === 403) {
        throw new CustomerAccountApiError(
          "permission",
          "\u3053\u306E\u60C5\u5831\u3092\u8868\u793A\u3059\u308B\u6A29\u9650\u304C\u3042\u308A\u307E\u305B\u3093\u3002",
          { status: 403 }
        );
      }
      if (!response.ok) {
        throw new CustomerAccountApiError(
          "network",
          "Shopify\u304B\u3089\u30C7\u30FC\u30BF\u3092\u53D6\u5F97\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002",
          { status: response.status }
        );
      }
      const body = await response.json();
      if (body.errors?.length) {
        const denied = body.errors.some((error) => [
          "ACCESS_DENIED",
          "FORBIDDEN"
        ].includes(error.extensions?.code));
        throw new CustomerAccountApiError(
          denied ? "permission" : "graphql",
          denied ? "\u3053\u306E\u60C5\u5831\u3092\u8868\u793A\u3059\u308B\u6A29\u9650\u304C\u3042\u308A\u307E\u305B\u3093\u3002" : "Shopify\u306E\u30C7\u30FC\u30BF\u3092\u51E6\u7406\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002"
        );
      }
      return body.data;
    };
    return {
      async getDashboard() {
        const data = await request(DASHBOARD_QUERY);
        return {
          customer: data.customer,
          orders: data.customer?.orders?.nodes ?? [],
          defaultAddress: data.customer?.defaultAddress ?? null
        };
      },
      async getOrders(after = null) {
        const data = await request(ORDERS_QUERY, { after });
        const connection = data.customer?.orders;
        return {
          orders: connection?.nodes ?? [],
          pageInfo: connection?.pageInfo ?? { hasNextPage: false, endCursor: null }
        };
      },
      async getOrder(orderId) {
        const data = await request(ORDER_QUERY, { orderId });
        const order = data.order ?? null;
        if (!order) return null;
        const firstPage = order.lineItems;
        if (!firstPage) return order;
        const nodes = [...firstPage.nodes ?? []];
        let pageInfo = firstPage.pageInfo;
        const requestedCursors = /* @__PURE__ */ new Set();
        while (pageInfo?.hasNextPage) {
          const after = pageInfo.endCursor;
          if (!after || requestedCursors.has(after)) {
            throw new CustomerAccountApiError(
              "graphql",
              "Shopify\u306E\u30C7\u30FC\u30BF\u3092\u51E6\u7406\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002"
            );
          }
          requestedCursors.add(after);
          const nextData = await request(ORDER_LINE_ITEMS_QUERY, {
            orderId,
            after
          });
          const nextPage = nextData.order?.lineItems;
          if (!nextPage) {
            throw new CustomerAccountApiError(
              "graphql",
              "Shopify\u306E\u30C7\u30FC\u30BF\u3092\u51E6\u7406\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002"
            );
          }
          nodes.push(...nextPage.nodes ?? []);
          pageInfo = nextPage.pageInfo;
        }
        return {
          ...order,
          lineItems: { ...firstPage, nodes, pageInfo }
        };
      },
      async getProfile() {
        const data = await request(PROFILE_QUERY);
        return data.customer;
      },
      async getAddresses() {
        const data = await request(ADDRESSES_QUERY);
        return {
          addresses: data.customer?.addresses?.nodes ?? [],
          defaultAddress: data.customer?.defaultAddress ?? null
        };
      },
      async updateProfile(input) {
        const data = await request(UPDATE_PROFILE_MUTATION, { input });
        return mutationResult(data.customerUpdate, "customer");
      },
      async createAddress(address, defaultAddress) {
        const data = await request(CREATE_ADDRESS_MUTATION, { address, defaultAddress });
        return mutationResult(data.customerAddressCreate, "customerAddress");
      },
      async updateAddress(addressId, address, defaultAddress) {
        const data = await request(UPDATE_ADDRESS_MUTATION, {
          addressId,
          address,
          defaultAddress
        });
        return mutationResult(data.customerAddressUpdate, "customerAddress");
      },
      async deleteAddress(addressId) {
        const data = await request(DELETE_ADDRESS_MUTATION, { addressId });
        return mutationResult(data.customerAddressDelete, "deletedAddressId");
      }
    };
  }

  // src/dom.js
  function element(document2, tag, options = {}, children = []) {
    const node = document2.createElement(tag);
    if (options.className) node.className = options.className;
    if (options.text !== void 0) node.textContent = String(options.text);
    for (const [name, value] of Object.entries(options.attrs ?? {})) {
      if (value !== null && value !== void 0 && value !== false) {
        node.setAttribute(name, value === true ? "" : String(value));
      }
    }
    for (const [name, value] of Object.entries(options.props ?? {})) {
      node[name] = value;
    }
    for (const [name, value] of Object.entries(options.dataset ?? {})) {
      node.dataset[name] = String(value);
    }
    for (const [name, listener] of Object.entries(options.on ?? {})) {
      node.addEventListener(name, listener);
    }
    node.append(...children.filter(Boolean));
    return node;
  }
  function replaceChildren(root, children) {
    root.replaceChildren(...children.filter(Boolean));
  }
  function safeExternalUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value);
      return url.protocol === "https:" ? url.toString() : null;
    } catch {
      return null;
    }
  }
  function withQuery(path, name, value) {
    const separator = String(path).includes("?") ? "&" : "?";
    return `${path}${separator}${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  }

  // src/router.js
  function decodeResourceId(value, resourceType) {
    const decoded = decodeURIComponent(value);
    if (!decoded.startsWith(`gid://shopify/${resourceType}/`)) {
      throw new Error(`${resourceType} ID\u304C\u4E0D\u6B63\u3067\u3059\u3002`);
    }
    return decoded;
  }
  function parseRoute(hash) {
    const path = String(hash || "").replace(/^#/, "");
    if (!path || path === "home") return { name: "home" };
    if (path === "orders") return { name: "orders" };
    if (path === "profile") return { name: "profile" };
    if (path === "addresses") return { name: "addresses" };
    if (path === "addresses/new") return { name: "address-new" };
    if (path.startsWith("orders/")) {
      try {
        return {
          name: "order-detail",
          orderId: decodeResourceId(path.slice("orders/".length), "Order")
        };
      } catch {
        return { name: "home" };
      }
    }
    if (path.startsWith("addresses/") && path.endsWith("/edit")) {
      try {
        return {
          name: "address-edit",
          addressId: decodeResourceId(
            path.slice("addresses/".length, -"/edit".length),
            "CustomerAddress"
          )
        };
      } catch {
        return { name: "home" };
      }
    }
    return { name: "home" };
  }
  function serializeRoute(route) {
    if (route.name === "order-detail") {
      return `#orders/${encodeURIComponent(route.orderId)}`;
    }
    if (route.name === "address-new") return "#addresses/new";
    if (route.name === "address-edit") {
      return `#addresses/${encodeURIComponent(route.addressId)}/edit`;
    }
    if (["home", "orders", "profile", "addresses"].includes(route.name)) {
      return `#${route.name}`;
    }
    return "#home";
  }
  function createRouter(windowLike, onRoute) {
    let started = false;
    const notify = () => onRoute(parseRoute(windowLike.location.hash));
    return {
      start() {
        if (started) return;
        started = true;
        windowLike.addEventListener("hashchange", notify);
        notify();
      },
      navigate(route) {
        windowLike.location.hash = serializeRoute(route);
      },
      stop() {
        if (!started) return;
        started = false;
        windowLike.removeEventListener("hashchange", notify);
      }
    };
  }

  // src/view-model.js
  var financialLabels = {
    AUTHORIZED: "\u652F\u6255\u3044\u627F\u8A8D\u6E08\u307F",
    PAID: "\u652F\u6255\u3044\u6E08\u307F",
    PARTIALLY_PAID: "\u4E00\u90E8\u652F\u6255\u3044\u6E08\u307F",
    PARTIALLY_REFUNDED: "\u4E00\u90E8\u8FD4\u91D1\u6E08\u307F",
    PENDING: "\u652F\u6255\u3044\u78BA\u8A8D\u4E2D",
    REFUNDED: "\u8FD4\u91D1\u6E08\u307F",
    VOIDED: "\u53D6\u6D88\u6E08\u307F"
  };
  var fulfillmentLabels = {
    FULFILLED: "\u767A\u9001\u6E08\u307F",
    IN_PROGRESS: "\u767A\u9001\u6E96\u5099\u4E2D",
    ON_HOLD: "\u767A\u9001\u4FDD\u7559",
    OPEN: "\u767A\u9001\u6E96\u5099\u4E2D",
    PARTIALLY_FULFILLED: "\u4E00\u90E8\u767A\u9001\u6E08\u307F",
    RESTOCKED: "\u8FD4\u54C1\u6E08\u307F",
    SCHEDULED: "\u767A\u9001\u4E88\u5B9A",
    UNFULFILLED: "\u767A\u9001\u6E96\u5099\u4E2D"
  };
  var fulfillmentDetailLabels = {
    CANCELLED: "\u30AD\u30E3\u30F3\u30BB\u30EB\u6E08\u307F",
    ERROR: "\u914D\u9001\u60C5\u5831\u3092\u78BA\u8A8D\u4E2D",
    FAILURE: "\u914D\u9001\u60C5\u5831\u3092\u78BA\u8A8D\u4E2D",
    OPEN: "\u914D\u9001\u6E96\u5099\u4E2D",
    PENDING: "\u914D\u9001\u6E96\u5099\u4E2D",
    SUCCESS: "\u767A\u9001\u6E08\u307F"
  };
  var prefectureNames = [
    "\u5317\u6D77\u9053",
    "\u9752\u68EE\u770C",
    "\u5CA9\u624B\u770C",
    "\u5BAE\u57CE\u770C",
    "\u79CB\u7530\u770C",
    "\u5C71\u5F62\u770C",
    "\u798F\u5CF6\u770C",
    "\u8328\u57CE\u770C",
    "\u6803\u6728\u770C",
    "\u7FA4\u99AC\u770C",
    "\u57FC\u7389\u770C",
    "\u5343\u8449\u770C",
    "\u6771\u4EAC\u90FD",
    "\u795E\u5948\u5DDD\u770C",
    "\u65B0\u6F5F\u770C",
    "\u5BCC\u5C71\u770C",
    "\u77F3\u5DDD\u770C",
    "\u798F\u4E95\u770C",
    "\u5C71\u68A8\u770C",
    "\u9577\u91CE\u770C",
    "\u5C90\u961C\u770C",
    "\u9759\u5CA1\u770C",
    "\u611B\u77E5\u770C",
    "\u4E09\u91CD\u770C",
    "\u6ECB\u8CC0\u770C",
    "\u4EAC\u90FD\u5E9C",
    "\u5927\u962A\u5E9C",
    "\u5175\u5EAB\u770C",
    "\u5948\u826F\u770C",
    "\u548C\u6B4C\u5C71\u770C",
    "\u9CE5\u53D6\u770C",
    "\u5CF6\u6839\u770C",
    "\u5CA1\u5C71\u770C",
    "\u5E83\u5CF6\u770C",
    "\u5C71\u53E3\u770C",
    "\u5FB3\u5CF6\u770C",
    "\u9999\u5DDD\u770C",
    "\u611B\u5A9B\u770C",
    "\u9AD8\u77E5\u770C",
    "\u798F\u5CA1\u770C",
    "\u4F50\u8CC0\u770C",
    "\u9577\u5D0E\u770C",
    "\u718A\u672C\u770C",
    "\u5927\u5206\u770C",
    "\u5BAE\u5D0E\u770C",
    "\u9E7F\u5150\u5CF6\u770C",
    "\u6C96\u7E04\u770C"
  ];
  var prefectureName = (zoneCode) => {
    const match = /^JP-(\d{2})$/.exec(String(zoneCode ?? ""));
    if (!match) return "";
    return prefectureNames[Number(match[1]) - 1] ?? "";
  };
  function formatMoney(money, locale = "ja-JP") {
    if (!money || money.amount === null || !money.currencyCode) return "\u2014";
    const amount = Number(money.amount);
    if (!Number.isFinite(amount)) return "\u2014";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: money.currencyCode
    }).format(amount);
  }
  function formatDate(value, locale = "ja-JP") {
    if (!value) return "\u2014";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "\u2014";
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Asia/Tokyo"
    }).format(date).replaceAll("/", ".");
  }
  function toCustomerModel(customer = {}) {
    const displayName = String(customer.displayName ?? "").trim();
    return {
      id: customer.id ?? null,
      firstName: customer.firstName ?? "",
      lastName: customer.lastName ?? "",
      displayName,
      email: customer.emailAddress?.emailAddress ?? "",
      initial: Array.from(displayName)[0] ?? "\u2014"
    };
  }
  var lineItemNodes = (order) => order?.lineItems?.nodes ?? [];
  function toOrderSummary(order = {}, locale = "ja-JP") {
    const lineItems = lineItemNodes(order);
    const firstItem = lineItems[0];
    const productSummary = firstItem ? `${firstItem.name} \xD7 ${firstItem.quantity}${lineItems.length > 1 ? `\u3001\u307B\u304B${lineItems.length - 1}\u70B9` : ""}` : "\u5546\u54C1\u60C5\u5831\u3092\u78BA\u8A8D\u4E2D";
    return {
      id: order.id ?? null,
      name: order.name ?? "\u6CE8\u6587\u756A\u53F7\u3092\u78BA\u8A8D\u4E2D",
      date: formatDate(order.processedAt, locale),
      paymentLabel: financialLabels[order.financialStatus] ?? "\u78BA\u8A8D\u4E2D",
      fulfillmentLabel: fulfillmentLabels[order.fulfillmentStatus] ?? "\u78BA\u8A8D\u4E2D",
      total: formatMoney(order.totalPrice, locale),
      productSummary,
      statusPageUrl: order.statusPageUrl ?? null
    };
  }
  function toAddressModel(address, defaultAddressId = null) {
    if (!address) return null;
    const values = {
      id: address.id ?? null,
      firstName: address.firstName ?? "",
      lastName: address.lastName ?? "",
      company: address.company ?? "",
      address1: address.address1 ?? "",
      address2: address.address2 ?? "",
      city: address.city ?? "",
      zoneCode: address.zoneCode ?? "",
      territoryCode: address.territoryCode ?? "JP",
      zip: address.zip ?? "",
      phoneNumber: address.phoneNumber ?? ""
    };
    const formatted = Array.isArray(address.formatted) ? address.formatted.filter(Boolean).join(" ") : "";
    const fallback = [
      values.zip ? `\u3012${values.zip}` : "",
      prefectureName(values.zoneCode),
      values.city,
      values.address1,
      values.address2,
      values.company,
      `${values.lastName} ${values.firstName}`.trim()
    ].filter(Boolean).join(" ");
    return {
      ...values,
      display: formatted || fallback || "\u4F4F\u6240\u60C5\u5831\u3092\u78BA\u8A8D\u4E2D",
      isDefault: Boolean(values.id && values.id === defaultAddressId)
    };
  }
  var moneyAmount = (money, currencyCode) => {
    if (!currencyCode || money?.currencyCode !== currencyCode) return 0;
    const amount = Number(money?.amount);
    return Number.isFinite(amount) ? amount : 0;
  };
  function toOrderDetail(order = {}, locale = "ja-JP") {
    const summary = toOrderSummary(order, locale);
    const currencyCode = order.totalPrice?.currencyCode;
    const lineItemDiscountAmount = lineItemNodes(order).reduce((total, lineItem) => total + moneyAmount(lineItem.totalDiscount, currencyCode), 0);
    const shippingDiscountAmount = (order.shippingDiscountAllocations ?? []).reduce((total, allocation) => total + moneyAmount(allocation.allocatedAmount, currencyCode), 0);
    const discountAmount = lineItemDiscountAmount + shippingDiscountAmount;
    return {
      ...summary,
      subtotal: formatMoney(order.subtotal, locale),
      shipping: formatMoney(order.totalShipping, locale),
      tax: formatMoney(order.totalTax, locale),
      discount: discountAmount > 0 ? formatMoney({
        amount: String(discountAmount),
        currencyCode
      }, locale) : null,
      lineItems: lineItemNodes(order).map((lineItem) => ({
        id: lineItem.id ?? null,
        name: lineItem.name ?? "\u5546\u54C1\u540D\u3092\u78BA\u8A8D\u4E2D",
        quantity: lineItem.quantity ?? 0,
        imageUrl: lineItem.image?.url ?? null,
        imageAlt: lineItem.image?.altText || lineItem.name || "",
        total: formatMoney(lineItem.totalPrice, locale)
      })),
      shippingAddress: toAddressModel(order.shippingAddress),
      fulfillments: (order.fulfillments?.nodes ?? []).map((fulfillment) => ({
        id: fulfillment.id ?? null,
        statusLabel: fulfillmentDetailLabels[fulfillment.status] ?? "\u914D\u9001\u72B6\u6CC1\u3092\u78BA\u8A8D\u4E2D",
        estimatedDeliveryDate: formatDate(fulfillment.estimatedDeliveryAt, locale),
        tracking: (fulfillment.trackingInformation ?? []).map((tracking) => ({
          number: tracking.number ?? "",
          url: tracking.url ?? null
        }))
      }))
    };
  }
  function toFieldErrorMap(errors = []) {
    return errors.reduce((map, error) => {
      const field = error.field?.at(-1) || "_form";
      map[field] = [...map[field] ?? [], error.message];
      return map;
    }, {});
  }

  // src/views/shell.js
  var actionButton = (document2, text, onClick, className = "mypage-button") => element(
    document2,
    "button",
    {
      className,
      text,
      props: { type: "button" },
      on: { click: onClick }
    }
  );
  var actionLink = (document2, text, href, className = "mypage-button") => element(
    document2,
    "a",
    { className, text, attrs: { href } }
  );
  function renderLoading(document2, label = "\u8AAD\u307F\u8FBC\u3093\u3067\u3044\u307E\u3059\u3002") {
    return element(document2, "p", {
      className: "mypage-loading",
      text: label,
      attrs: { role: "status", "aria-live": "polite" }
    });
  }
  function renderError(document2, {
    title = "\u60C5\u5831\u3092\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F",
    message = "\u6642\u9593\u3092\u304A\u3044\u3066\u3001\u3082\u3046\u4E00\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002",
    onRetry,
    onBack,
    standardAccountUrl
  } = {}) {
    const actions = [
      onRetry ? actionButton(document2, "\u3082\u3046\u4E00\u5EA6\u8A66\u3059", onRetry) : null,
      onBack ? actionButton(document2, "\u4E00\u89A7\u3078\u623B\u308B", onBack, "mypage-button mypage-button--secondary") : null,
      standardAccountUrl ? actionLink(document2, "Shopify\u6A19\u6E96\u30DE\u30A4\u30DA\u30FC\u30B8\u3092\u958B\u304F", standardAccountUrl, "mypage-button mypage-button--secondary") : null
    ];
    return element(document2, "section", {
      className: "mypage-error",
      attrs: { role: "alert" }
    }, [
      element(document2, "h2", { className: "mypage-error__title", text: title }),
      element(document2, "p", { className: "mypage-error__message", text: message }),
      element(document2, "div", { className: "mypage-error__actions" }, actions)
    ]);
  }
  function createOrderCard(document2, order, { onDetail, contactUrl } = {}) {
    const actions = [];
    if (onDetail) {
      actions.push(actionButton(
        document2,
        "\u6CE8\u6587\u8A73\u7D30",
        onDetail,
        "order-card__button"
      ));
    }
    if (contactUrl) {
      actions.push(actionLink(
        document2,
        "\u554F\u3044\u5408\u308F\u305B",
        withQuery(contactUrl, "order", order.name),
        "order-card__button"
      ));
    }
    const statusPageUrl = safeExternalUrl(order.statusPageUrl);
    if (statusPageUrl) {
      actions.push(actionLink(
        document2,
        "\u914D\u9001\u72B6\u6CC1",
        statusPageUrl,
        "order-card__button"
      ));
    }
    return element(document2, "article", { className: "order-card" }, [
      element(document2, "div", { className: "order-card__header" }, [
        element(document2, "div", {}, [
          element(document2, "p", { className: "order-card__number", text: `\u6CE8\u6587 ${order.name}` }),
          element(document2, "p", { className: "order-card__date", text: `\u6CE8\u6587\u65E5\uFF1A${order.date}` })
        ]),
        element(document2, "span", { className: "order-card__status", text: order.fulfillmentLabel })
      ]),
      element(document2, "div", { className: "order-card__body" }, [
        element(document2, "p", { className: "order-card__product", text: order.productSummary }),
        element(document2, "p", { className: "order-card__price", text: `\u5408\u8A08 ${order.total}` }),
        element(document2, "p", { className: "order-card__payment", text: order.paymentLabel })
      ]),
      actions.length > 0 ? element(document2, "div", { className: "order-card__actions" }, actions) : null
    ]);
  }

  // src/views/addresses.js
  var prefectures = [
    "\u5317\u6D77\u9053",
    "\u9752\u68EE\u770C",
    "\u5CA9\u624B\u770C",
    "\u5BAE\u57CE\u770C",
    "\u79CB\u7530\u770C",
    "\u5C71\u5F62\u770C",
    "\u798F\u5CF6\u770C",
    "\u8328\u57CE\u770C",
    "\u6803\u6728\u770C",
    "\u7FA4\u99AC\u770C",
    "\u57FC\u7389\u770C",
    "\u5343\u8449\u770C",
    "\u6771\u4EAC\u90FD",
    "\u795E\u5948\u5DDD\u770C",
    "\u65B0\u6F5F\u770C",
    "\u5BCC\u5C71\u770C",
    "\u77F3\u5DDD\u770C",
    "\u798F\u4E95\u770C",
    "\u5C71\u68A8\u770C",
    "\u9577\u91CE\u770C",
    "\u5C90\u961C\u770C",
    "\u9759\u5CA1\u770C",
    "\u611B\u77E5\u770C",
    "\u4E09\u91CD\u770C",
    "\u6ECB\u8CC0\u770C",
    "\u4EAC\u90FD\u5E9C",
    "\u5927\u962A\u5E9C",
    "\u5175\u5EAB\u770C",
    "\u5948\u826F\u770C",
    "\u548C\u6B4C\u5C71\u770C",
    "\u9CE5\u53D6\u770C",
    "\u5CF6\u6839\u770C",
    "\u5CA1\u5C71\u770C",
    "\u5E83\u5CF6\u770C",
    "\u5C71\u53E3\u770C",
    "\u5FB3\u5CF6\u770C",
    "\u9999\u5DDD\u770C",
    "\u611B\u5A9B\u770C",
    "\u9AD8\u77E5\u770C",
    "\u798F\u5CA1\u770C",
    "\u4F50\u8CC0\u770C",
    "\u9577\u5D0E\u770C",
    "\u718A\u672C\u770C",
    "\u5927\u5206\u770C",
    "\u5BAE\u5D0E\u770C",
    "\u9E7F\u5150\u5CF6\u770C",
    "\u6C96\u7E04\u770C"
  ];
  var valueOf = (form, name) => String(form.elements.namedItem(name)?.value ?? "").trim();
  function addressInputFromForm(form) {
    return {
      address: {
        territoryCode: "JP",
        zoneCode: valueOf(form, "zoneCode"),
        zip: valueOf(form, "zip"),
        city: valueOf(form, "city"),
        address1: valueOf(form, "address1"),
        address2: valueOf(form, "address2"),
        lastName: valueOf(form, "lastName"),
        firstName: valueOf(form, "firstName"),
        company: valueOf(form, "company"),
        phoneNumber: valueOf(form, "phoneNumber")
      },
      defaultAddress: Boolean(form.elements.namedItem("defaultAddress")?.checked)
    };
  }
  async function confirmAndDelete(address, onDelete, confirmImpl) {
    if (!confirmImpl(`${address.display} \u3092\u524A\u9664\u3057\u307E\u3059\u304B\uFF1F`)) return false;
    await onDelete(address.id);
    return true;
  }
  var fieldError = (document2, name, errors) => errors?.length ? element(document2, "p", {
    className: "mypage-field__error",
    text: errors.join(" "),
    attrs: { id: `address-${name}-error` }
  }) : null;
  var textField = (document2, { name, label, value, errors, required: required2 = false, autoComplete }) => {
    const errorId = errors?.length ? `address-${name}-error` : null;
    return element(document2, "div", { className: "mypage-field" }, [
      element(document2, "label", { className: "mypage-field__label", text: label, attrs: { for: `address-${name}` } }),
      element(document2, "input", {
        className: "mypage-field__input",
        attrs: {
          id: `address-${name}`,
          name,
          type: "text",
          required: required2,
          autocomplete: autoComplete,
          "aria-describedby": errorId,
          "aria-invalid": errorId ? "true" : null
        },
        props: { value }
      }),
      fieldError(document2, name, errors)
    ]);
  };
  function renderAddresses(document2, {
    addresses,
    actions,
    confirmImpl = globalThis.confirm?.bind(globalThis) ?? (() => false)
  }) {
    const cards = addresses.length > 0 ? addresses.map((address) => element(document2, "article", { className: "address-card" }, [
      element(document2, "div", { className: "address-card__body" }, [
        element(document2, "p", { className: "address-card__address", text: address.display }),
        address.phoneNumber ? element(document2, "p", { className: "address-card__phone", text: address.phoneNumber }) : null,
        address.isDefault ? element(document2, "span", { className: "address-card__badge", text: "\u30C7\u30D5\u30A9\u30EB\u30C8\u4F4F\u6240" }) : null
      ]),
      element(document2, "div", { className: "address-card__actions" }, [
        actionButton(
          document2,
          "\u7DE8\u96C6",
          () => actions.navigate({ name: "address-edit", addressId: address.id }),
          "mypage-button mypage-button--secondary"
        ),
        actionButton(
          document2,
          "\u524A\u9664",
          () => {
            void confirmAndDelete(address, actions.delete, confirmImpl);
          },
          "mypage-text-button mypage-text-button--danger"
        )
      ])
    ])) : [element(document2, "p", { className: "mypage-empty", text: "\u767B\u9332\u4F4F\u6240\u306F\u307E\u3060\u3042\u308A\u307E\u305B\u3093\u3002" })];
    return element(document2, "section", { className: "mypage-view mypage-addresses" }, [
      element(document2, "div", { className: "mypage-view__header" }, [
        element(document2, "h2", { className: "mypage-section__title", text: "\u4F4F\u6240\u7BA1\u7406" }),
        actionButton(document2, "\u30DE\u30A4\u30DA\u30FC\u30B8\u3078\u623B\u308B", () => actions.navigate({ name: "home" }), "mypage-text-button")
      ]),
      element(document2, "div", { className: "address-list" }, cards),
      actionButton(document2, "\u65B0\u3057\u3044\u4F4F\u6240\u3092\u8FFD\u52A0", () => actions.navigate({ name: "address-new" }), "mypage-button")
    ]);
  }
  function renderAddressForm(document2, {
    mode,
    address = {},
    saving,
    fieldErrors = {},
    actions
  }) {
    const allErrors = Object.values(fieldErrors).flat();
    const options = [
      element(document2, "option", { text: "\u9078\u629E\u3057\u3066\u304F\u3060\u3055\u3044", attrs: { value: "" } }),
      ...prefectures.map((name, index) => {
        const value = `JP-${String(index + 1).padStart(2, "0")}`;
        return element(document2, "option", {
          text: name,
          attrs: { value },
          props: { selected: address.zoneCode === value }
        });
      })
    ];
    const zoneErrorId = fieldErrors.zoneCode?.length ? "address-zoneCode-error" : null;
    const form = element(document2, "form", {
      className: "mypage-form",
      on: {
        submit(event) {
          event.preventDefault();
          void actions.submit(addressInputFromForm(event.currentTarget));
        }
      }
    }, [
      allErrors.length > 0 ? element(document2, "div", {
        className: "mypage-form__error-summary",
        text: allErrors.join(" "),
        attrs: { role: "alert" }
      }) : null,
      element(document2, "div", { className: "mypage-form__grid" }, [
        textField(document2, { name: "lastName", label: "\u59D3", value: address.lastName ?? "", errors: fieldErrors.lastName, required: true, autoComplete: "family-name" }),
        textField(document2, { name: "firstName", label: "\u540D", value: address.firstName ?? "", errors: fieldErrors.firstName, required: true, autoComplete: "given-name" }),
        textField(document2, { name: "zip", label: "\u90F5\u4FBF\u756A\u53F7", value: address.zip ?? "", errors: fieldErrors.zip, required: true, autoComplete: "postal-code" }),
        element(document2, "div", { className: "mypage-field" }, [
          element(document2, "label", { className: "mypage-field__label", text: "\u90FD\u9053\u5E9C\u770C", attrs: { for: "address-zoneCode" } }),
          element(document2, "select", {
            className: "mypage-field__input",
            attrs: {
              id: "address-zoneCode",
              name: "zoneCode",
              required: true,
              autocomplete: "address-level1",
              "aria-describedby": zoneErrorId,
              "aria-invalid": zoneErrorId ? "true" : null
            }
          }, options),
          fieldError(document2, "zoneCode", fieldErrors.zoneCode)
        ])
      ]),
      textField(document2, { name: "city", label: "\u5E02\u533A\u753A\u6751", value: address.city ?? "", errors: fieldErrors.city, required: true, autoComplete: "address-level2" }),
      textField(document2, { name: "address1", label: "\u4F4F\u62401", value: address.address1 ?? "", errors: fieldErrors.address1, required: true, autoComplete: "address-line1" }),
      textField(document2, { name: "address2", label: "\u4F4F\u62402", value: address.address2 ?? "", errors: fieldErrors.address2, autoComplete: "address-line2" }),
      textField(document2, { name: "company", label: "\u4F1A\u793E\u540D", value: address.company ?? "", errors: fieldErrors.company, autoComplete: "organization" }),
      textField(document2, { name: "phoneNumber", label: "\u96FB\u8A71\u756A\u53F7", value: address.phoneNumber ?? "", errors: fieldErrors.phoneNumber, autoComplete: "tel" }),
      element(document2, "label", { className: "mypage-checkbox" }, [
        element(document2, "input", {
          attrs: { type: "checkbox", name: "defaultAddress" },
          props: { checked: Boolean(address.isDefault) }
        }),
        element(document2, "span", { text: "\u30C7\u30D5\u30A9\u30EB\u30C8\u4F4F\u6240\u306B\u3059\u308B" })
      ]),
      element(document2, "div", { className: "mypage-form__actions" }, [
        element(document2, "button", {
          className: "mypage-button",
          text: saving ? "\u4FDD\u5B58\u4E2D\u2026" : "\u4FDD\u5B58\u3059\u308B",
          attrs: { type: "submit" },
          props: { disabled: Boolean(saving) }
        }),
        actionButton(document2, "\u30AD\u30E3\u30F3\u30BB\u30EB", () => actions.navigate({ name: "addresses" }), "mypage-button mypage-button--secondary")
      ])
    ]);
    return element(document2, "section", { className: "mypage-view mypage-address-form" }, [
      element(document2, "h2", {
        className: "mypage-section__title",
        text: mode === "edit" ? "\u4F4F\u6240\u3092\u7DE8\u96C6" : "\u4F4F\u6240\u3092\u8FFD\u52A0"
      }),
      element(document2, "div", { className: "mypage-panel" }, [form])
    ]);
  }

  // src/views/dashboard.js
  function renderDashboard(document2, { customer, orders, config, actions }) {
    const orderContent = orders.length > 0 ? orders.slice(0, 3).map((order) => createOrderCard(document2, order, {
      onDetail: () => actions.navigate({ name: "order-detail", orderId: order.id }),
      contactUrl: config.contactUrl
    })) : [element(document2, "div", { className: "mypage-empty" }, [
      element(document2, "p", { text: "\u307E\u3060\u6CE8\u6587\u306F\u3042\u308A\u307E\u305B\u3093\u3002" }),
      actionLink(document2, "\u5546\u54C1\u3092\u898B\u308B", config.shopUrl)
    ])];
    const accountActions = element(document2, "div", { className: "account-card__actions" }, [
      actionButton(
        document2,
        "\u30D7\u30ED\u30D5\u30A3\u30FC\u30EB\u3092\u7DE8\u96C6",
        () => actions.navigate({ name: "profile" }),
        "account-card__edit"
      ),
      actionButton(
        document2,
        "\u6CE8\u6587\u5C65\u6B74",
        () => actions.navigate({ name: "orders" }),
        "account-card__link"
      ),
      actionButton(
        document2,
        "\u4F4F\u6240\u7BA1\u7406",
        () => actions.navigate({ name: "addresses" }),
        "account-card__link"
      ),
      actionButton(document2, "\u30ED\u30B0\u30A2\u30A6\u30C8", actions.logout, "account-card__logout")
    ]);
    return element(document2, "div", { className: "mypage__layout" }, [
      element(document2, "div", { className: "mypage__main" }, [
        element(document2, "section", { className: "mypage-section" }, [
          element(document2, "h2", { className: "mypage-section__title", text: "\u6700\u8FD1\u306E\u6CE8\u6587" }),
          element(document2, "div", { className: "mypage-orders" }, orderContent)
        ])
      ]),
      element(document2, "aside", { className: "account-card" }, [
        element(document2, "h2", { className: "account-card__title", text: "\u30A2\u30AB\u30A6\u30F3\u30C8" }),
        element(document2, "div", { className: "account-card__user" }, [
          element(document2, "div", { className: "account-card__avatar", text: customer.initial, attrs: { "aria-hidden": "true" } }),
          element(document2, "div", { className: "account-card__user-info" }, [
            element(document2, "p", { className: "account-card__name", text: customer.displayName || "\u304A\u5BA2\u69D8" }),
            element(document2, "p", { className: "account-card__email", text: customer.email || "\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9\u306FShopify\u3067\u78BA\u8A8D\u3067\u304D\u307E\u3059" })
          ])
        ]),
        accountActions
      ])
    ]);
  }

  // src/views/order-detail.js
  var moneyRow = (document2, label, value, className = "") => element(
    document2,
    "div",
    { className: `order-detail__money-row ${className}`.trim() },
    [
      element(document2, "dt", { text: label }),
      element(document2, "dd", { text: value })
    ]
  );
  function renderOrderDetail(document2, { order, config, actions }) {
    const lineItems = order.lineItems.map((lineItem) => {
      const imageUrl = safeExternalUrl(lineItem.imageUrl);
      return element(document2, "li", { className: "order-detail__line-item" }, [
        imageUrl ? element(document2, "img", {
          className: "order-detail__image",
          attrs: { src: imageUrl, alt: lineItem.imageAlt, width: "96", height: "96", loading: "lazy" }
        }) : null,
        element(document2, "div", { className: "order-detail__line-info" }, [
          element(document2, "p", { className: "order-detail__line-name", text: lineItem.name }),
          element(document2, "p", { className: "order-detail__line-quantity", text: `\u6570\u91CF\uFF1A${lineItem.quantity}` })
        ]),
        element(document2, "p", { className: "order-detail__line-price", text: lineItem.total })
      ]);
    });
    const fulfillmentNodes = order.fulfillments.map((fulfillment) => element(
      document2,
      "article",
      { className: "order-detail__fulfillment" },
      [
        element(document2, "p", { className: "order-detail__fulfillment-status", text: fulfillment.statusLabel }),
        fulfillment.estimatedDeliveryDate !== "\u2014" ? element(document2, "p", { text: `\u304A\u5C4A\u3051\u4E88\u5B9A\uFF1A${fulfillment.estimatedDeliveryDate}` }) : null,
        ...fulfillment.tracking.map((tracking) => {
          const trackingUrl = safeExternalUrl(tracking.url);
          return trackingUrl ? actionLink(document2, tracking.number || "\u914D\u9001\u72B6\u6CC1\u3092\u78BA\u8A8D", trackingUrl, "order-detail__tracking") : element(document2, "p", { text: tracking.number || "\u8FFD\u8DE1\u756A\u53F7\u3092\u78BA\u8A8D\u4E2D" });
        })
      ]
    ));
    const statusPageUrl = safeExternalUrl(order.statusPageUrl);
    return element(document2, "section", { className: "mypage-view order-detail" }, [
      element(document2, "div", { className: "mypage-view__header" }, [
        element(document2, "div", {}, [
          element(document2, "p", { className: "order-detail__eyebrow", text: `\u6CE8\u6587 ${order.name}` }),
          element(document2, "h2", { className: "mypage-section__title", text: "\u6CE8\u6587\u8A73\u7D30" }),
          element(document2, "p", { className: "order-detail__date", text: `\u6CE8\u6587\u65E5\uFF1A${order.date}` })
        ]),
        actionButton(
          document2,
          "\u6CE8\u6587\u5C65\u6B74\u3078\u623B\u308B",
          () => actions.navigate({ name: "orders" }),
          "mypage-text-button"
        )
      ]),
      element(document2, "div", { className: "order-detail__status-row" }, [
        element(document2, "span", { className: "order-card__status", text: order.fulfillmentLabel }),
        element(document2, "span", { className: "order-detail__payment", text: order.paymentLabel })
      ]),
      element(document2, "section", { className: "mypage-panel" }, [
        element(document2, "h3", { className: "mypage-panel__title", text: "\u5546\u54C1\u660E\u7D30" }),
        element(document2, "ul", { className: "order-detail__line-items" }, lineItems),
        element(document2, "dl", { className: "order-detail__money" }, [
          moneyRow(document2, "\u5C0F\u8A08", order.subtotal),
          order.discount ? moneyRow(document2, "\u5272\u5F15", `\u2212${order.discount}`) : null,
          moneyRow(document2, "\u9001\u6599", order.shipping),
          moneyRow(document2, "\u7A0E", order.tax),
          moneyRow(document2, "\u5408\u8A08", order.total, "order-detail__money-row--total")
        ])
      ]),
      order.shippingAddress ? element(document2, "section", { className: "mypage-panel" }, [
        element(document2, "h3", { className: "mypage-panel__title", text: "\u914D\u9001\u5148" }),
        element(document2, "p", { className: "order-detail__address", text: order.shippingAddress.display })
      ]) : null,
      fulfillmentNodes.length > 0 ? element(document2, "section", { className: "mypage-panel" }, [
        element(document2, "h3", { className: "mypage-panel__title", text: "\u914D\u9001\u72B6\u6CC1" }),
        ...fulfillmentNodes
      ]) : null,
      element(document2, "div", { className: "order-detail__actions" }, [
        statusPageUrl ? actionLink(document2, "\u6CE8\u6587\u72B6\u6CC1\u3092\u958B\u304F", statusPageUrl) : null,
        actionLink(
          document2,
          "\u3053\u306E\u6CE8\u6587\u306B\u3064\u3044\u3066\u554F\u3044\u5408\u308F\u305B\u308B",
          withQuery(config.contactUrl, "order", order.name),
          "mypage-button mypage-button--contact"
        )
      ])
    ]);
  }

  // src/views/orders.js
  function renderOrders(document2, {
    orders,
    pageInfo,
    loadingMore,
    actions,
    contactUrl
  }) {
    const cards = orders.length > 0 ? orders.map((order) => createOrderCard(document2, order, {
      onDetail: () => actions.navigate({ name: "order-detail", orderId: order.id }),
      contactUrl
    })) : [element(document2, "p", { className: "mypage-empty", text: "\u6CE8\u6587\u5C65\u6B74\u306F\u307E\u3060\u3042\u308A\u307E\u305B\u3093\u3002" })];
    const loadMore = pageInfo.hasNextPage ? actionButton(
      document2,
      loadingMore ? "\u8AAD\u307F\u8FBC\u307F\u4E2D\u2026" : "\u3055\u3089\u306B\u8AAD\u307F\u8FBC\u3080",
      actions.loadMore,
      "mypage-button mypage-button--load-more"
    ) : null;
    if (loadMore) loadMore.disabled = Boolean(loadingMore);
    return element(document2, "section", { className: "mypage-view mypage-orders-view" }, [
      element(document2, "div", { className: "mypage-view__header" }, [
        element(document2, "h2", { className: "mypage-section__title", text: "\u6CE8\u6587\u5C65\u6B74" }),
        actionButton(
          document2,
          "\u30DE\u30A4\u30DA\u30FC\u30B8\u3078\u623B\u308B",
          () => actions.navigate({ name: "home" }),
          "mypage-text-button"
        )
      ]),
      element(document2, "div", { className: "mypage-orders" }, cards),
      loadMore ? element(document2, "div", { className: "mypage-pagination" }, [loadMore]) : null
    ]);
  }

  // src/views/profile.js
  var valueOf2 = (form, name) => String(form.elements.namedItem(name)?.value ?? "").trim();
  function profileInputFromForm(form) {
    return {
      firstName: valueOf2(form, "firstName"),
      lastName: valueOf2(form, "lastName")
    };
  }
  var fieldError2 = (document2, name, errors) => errors?.length ? element(document2, "p", {
    className: "mypage-field__error",
    text: errors.join(" "),
    attrs: { id: `profile-${name}-error` }
  }) : null;
  var textField2 = (document2, {
    name,
    label,
    value,
    errors,
    readOnly = false,
    autoComplete
  }) => {
    const errorId = errors?.length ? `profile-${name}-error` : null;
    return element(document2, "div", { className: "mypage-field" }, [
      element(document2, "label", { className: "mypage-field__label", text: label, attrs: { for: `profile-${name}` } }),
      element(document2, "input", {
        className: "mypage-field__input",
        attrs: {
          id: `profile-${name}`,
          name,
          type: "text",
          autocomplete: autoComplete,
          readonly: readOnly,
          "aria-describedby": errorId,
          "aria-invalid": errorId ? "true" : null
        },
        props: { value }
      }),
      fieldError2(document2, name, errors)
    ]);
  };
  function renderProfile(document2, {
    customer,
    saving,
    fieldErrors = {},
    standardAccountUrl,
    actions
  }) {
    const allErrors = Object.values(fieldErrors).flat();
    const form = element(document2, "form", {
      className: "mypage-form",
      on: {
        submit(event) {
          event.preventDefault();
          void actions.submit(profileInputFromForm(event.currentTarget));
        }
      }
    }, [
      allErrors.length > 0 ? element(document2, "div", {
        className: "mypage-form__error-summary",
        text: allErrors.join(" "),
        attrs: { role: "alert" }
      }) : null,
      element(document2, "div", { className: "mypage-form__grid" }, [
        textField2(document2, {
          name: "lastName",
          label: "\u59D3",
          value: customer.lastName,
          errors: fieldErrors.lastName,
          autoComplete: "family-name"
        }),
        textField2(document2, {
          name: "firstName",
          label: "\u540D",
          value: customer.firstName,
          errors: fieldErrors.firstName,
          autoComplete: "given-name"
        })
      ]),
      textField2(document2, {
        name: "email",
        label: "\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9",
        value: customer.email,
        errors: fieldErrors.email,
        readOnly: true,
        autoComplete: "email"
      }),
      element(document2, "p", {
        className: "mypage-form__hint"
      }, [
        actionLink(
          document2,
          "\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9\u306E\u5909\u66F4\u306F\u3053\u3061\u3089",
          standardAccountUrl,
          "mypage-form__hint-link"
        )
      ]),
      element(document2, "div", { className: "mypage-form__actions" }, [
        element(document2, "button", {
          className: "mypage-button",
          text: saving ? "\u4FDD\u5B58\u4E2D\u2026" : "\u4FDD\u5B58\u3059\u308B",
          attrs: { type: "submit" },
          props: { disabled: Boolean(saving) }
        })
      ])
    ]);
    return element(document2, "section", { className: "mypage-view mypage-profile" }, [
      element(document2, "div", { className: "mypage-view__header" }, [
        element(document2, "h2", { className: "mypage-section__title", text: "\u30D7\u30ED\u30D5\u30A3\u30FC\u30EB" }),
        actionButton(document2, "\u30DE\u30A4\u30DA\u30FC\u30B8\u3078\u623B\u308B", () => actions.navigate({ name: "home" }), "mypage-text-button")
      ]),
      element(document2, "div", { className: "mypage-panel" }, [form])
    ]);
  }

  // src/controller.js
  var uniqueOrders = (orders) => [...new Map(orders.map((order) => [order.id, order])).values()];
  var errorMessage = (error) => {
    if (error?.kind === "permission") {
      return "\u3053\u306E\u60C5\u5831\u3092\u8868\u793A\u3059\u308B\u305F\u3081\u306E\u6A29\u9650\u3092\u78BA\u8A8D\u3057\u3066\u3044\u307E\u3059\u3002\u6642\u9593\u3092\u304A\u3044\u3066\u518D\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002";
    }
    if (error?.kind === "graphql") {
      return "Shopify\u306E\u60C5\u5831\u3092\u51E6\u7406\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002\u6642\u9593\u3092\u304A\u3044\u3066\u518D\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002";
    }
    return error?.message || "\u901A\u4FE1\u306B\u5931\u6557\u3057\u307E\u3057\u305F\u3002\u6642\u9593\u3092\u304A\u3044\u3066\u518D\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002";
  };
  function createAccountController({
    auth,
    apiFactory,
    routerFactory,
    views,
    config
  }) {
    let api;
    let router;
    let generation = 0;
    let stopped = false;
    const navigate = (route) => router.navigate(route);
    const commonActions = () => ({
      navigate,
      logout: () => {
        void auth.logout().catch((error) => {
          if (error instanceof AuthRedirectError) return;
          views.error({
            title: "\u30ED\u30B0\u30A2\u30A6\u30C8\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F",
            message: errorMessage(error),
            standardAccountUrl: config.standardAccountUrl
          }, {});
        });
      }
    });
    const handleError = async (error, route) => {
      if (error instanceof AuthRedirectError) throw error;
      if (error instanceof CustomerAccountApiError && error.kind === "unauthorized") {
        return auth.reauthenticateOnce();
      }
      views.error({
        title: error?.kind === "permission" ? "\u8868\u793A\u6A29\u9650\u3092\u78BA\u8A8D\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F" : "\u60C5\u5831\u3092\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F",
        message: errorMessage(error),
        standardAccountUrl: config.standardAccountUrl
      }, {
        retry: () => renderRoute(route),
        back: route.name === "home" ? null : () => navigate({ name: "home" })
      });
      return void 0;
    };
    const renderHome = async (routeGeneration) => {
      const data = await api.getDashboard();
      if (routeGeneration !== generation || stopped) return;
      auth.markApiSuccess();
      views.dashboard({
        customer: toCustomerModel(data.customer),
        orders: data.orders.map((item) => toOrderSummary(item, config.locale))
      }, commonActions());
    };
    const renderOrderList = async (routeGeneration) => {
      const data = await api.getOrders(null);
      if (routeGeneration !== generation || stopped) return;
      auth.markApiSuccess();
      let orders = data.orders.map((item) => toOrderSummary(item, config.locale));
      let pageInfo = data.pageInfo;
      let loadingMore = false;
      const render = () => views.orders(
        { orders, pageInfo, loadingMore },
        {
          ...commonActions(),
          loadMore: async () => {
            if (loadingMore || !pageInfo.hasNextPage) return;
            loadingMore = true;
            render();
            try {
              const next = await api.getOrders(pageInfo.endCursor);
              if (routeGeneration !== generation || stopped) return;
              orders = uniqueOrders([
                ...orders,
                ...next.orders.map((item) => toOrderSummary(item, config.locale))
              ]);
              pageInfo = next.pageInfo;
              auth.markApiSuccess();
            } catch (error) {
              loadingMore = false;
              await handleError(error, { name: "orders" });
              return;
            }
            loadingMore = false;
            render();
          }
        }
      );
      render();
    };
    const renderOrder = async (route, routeGeneration) => {
      const order = await api.getOrder(route.orderId);
      if (routeGeneration !== generation || stopped) return;
      auth.markApiSuccess();
      if (!order) {
        navigate({ name: "orders" });
        return;
      }
      views.orderDetail(
        { order: toOrderDetail(order, config.locale) },
        commonActions()
      );
    };
    const renderProfileScreen = async (routeGeneration) => {
      const rawCustomer = await api.getProfile();
      if (routeGeneration !== generation || stopped) return;
      auth.markApiSuccess();
      let customer = toCustomerModel(rawCustomer);
      const render = ({ saving = false, fieldErrors = {} } = {}) => views.profile(
        { customer, saving, fieldErrors, standardAccountUrl: config.standardAccountUrl },
        {
          ...commonActions(),
          submit: async (input) => {
            render({ saving: true });
            try {
              const updated = await api.updateProfile(input);
              if (routeGeneration !== generation || stopped) return;
              customer = toCustomerModel({ ...rawCustomer, ...updated });
              auth.markApiSuccess();
              render();
            } catch (error) {
              if (error?.kind === "unauthorized") {
                await handleError(error, { name: "profile" });
                return;
              }
              customer = { ...customer, ...input };
              const fieldErrors2 = error?.kind === "validation" ? toFieldErrorMap(error.fieldErrors) : { _form: [errorMessage(error)] };
              render({ fieldErrors: fieldErrors2 });
            }
          }
        }
      );
      render();
    };
    const loadAddressModels = async () => {
      const data = await api.getAddresses();
      const defaultId = data.defaultAddress?.id ?? null;
      return data.addresses.map((address) => toAddressModel(address, defaultId));
    };
    const renderAddressList = async (routeGeneration) => {
      const addresses = await loadAddressModels();
      if (routeGeneration !== generation || stopped) return;
      auth.markApiSuccess();
      views.addresses({ addresses }, {
        ...commonActions(),
        delete: async (addressId) => {
          try {
            await api.deleteAddress(addressId);
            auth.markApiSuccess();
            await renderRoute({ name: "addresses" });
          } catch (error) {
            await handleError(error, { name: "addresses" });
          }
        }
      });
    };
    const renderAddressEditor = async (route, routeGeneration) => {
      let address = {};
      if (route.name === "address-edit") {
        const addresses = await loadAddressModels();
        if (routeGeneration !== generation || stopped) return;
        auth.markApiSuccess();
        address = addresses.find((item) => item.id === route.addressId) ?? null;
        if (!address) {
          navigate({ name: "addresses" });
          return;
        }
      }
      const render = ({ saving = false, fieldErrors = {} } = {}) => views.addressForm(
        {
          mode: route.name === "address-edit" ? "edit" : "new",
          address,
          saving,
          fieldErrors
        },
        {
          ...commonActions(),
          submit: async ({ address: input, defaultAddress }) => {
            render({ saving: true });
            try {
              if (route.name === "address-edit") {
                await api.updateAddress(route.addressId, input, defaultAddress);
              } else {
                await api.createAddress(input, defaultAddress);
              }
              if (routeGeneration !== generation || stopped) return;
              auth.markApiSuccess();
              navigate({ name: "addresses" });
            } catch (error) {
              if (error?.kind === "unauthorized") {
                await handleError(error, route);
                return;
              }
              address = { ...address ?? {}, ...input, isDefault: defaultAddress };
              const fieldErrors2 = error?.kind === "validation" ? toFieldErrorMap(error.fieldErrors) : { _form: [errorMessage(error)] };
              render({ fieldErrors: fieldErrors2 });
            }
          }
        }
      );
      render();
    };
    async function renderRoute(route) {
      const routeGeneration = ++generation;
      views.loading({ label: "\u30A2\u30AB\u30A6\u30F3\u30C8\u60C5\u5831\u3092\u8AAD\u307F\u8FBC\u3093\u3067\u3044\u307E\u3059\u3002" });
      try {
        if (route.name === "home") return await renderHome(routeGeneration);
        if (route.name === "orders") return await renderOrderList(routeGeneration);
        if (route.name === "order-detail") return await renderOrder(route, routeGeneration);
        if (route.name === "profile") return await renderProfileScreen(routeGeneration);
        if (route.name === "addresses") return await renderAddressList(routeGeneration);
        if (route.name === "address-new" || route.name === "address-edit") {
          return await renderAddressEditor(route, routeGeneration);
        }
        navigate({ name: "home" });
        return void 0;
      } catch (error) {
        if (routeGeneration !== generation || stopped) return void 0;
        return handleError(error, route);
      }
    }
    return {
      async start() {
        const accessToken = await auth.getAccessToken();
        const endpoint = await auth.getApiEndpoint();
        api = apiFactory({ endpoint, accessToken });
        router = routerFactory((route) => {
          void renderRoute(route).catch((error) => {
            if (!(error instanceof AuthRedirectError)) {
              views.error({
                title: "\u60C5\u5831\u3092\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F",
                message: errorMessage(error),
                standardAccountUrl: config.standardAccountUrl
              }, {});
            }
          });
        });
        router.start();
      },
      stop() {
        stopped = true;
        generation += 1;
        router?.stop();
      },
      renderRoute
    };
  }
  var createDomViews = (document2, root, config) => {
    const mount = (node) => replaceChildren(root, [node]);
    return {
      loading: ({ label }) => mount(renderLoading(document2, label)),
      error: (model, actions) => mount(renderError(document2, {
        ...model,
        onRetry: actions.retry,
        onBack: actions.back
      })),
      dashboard: (model, actions) => mount(renderDashboard(document2, {
        ...model,
        config,
        actions
      })),
      orders: (model, actions) => mount(renderOrders(document2, {
        ...model,
        actions,
        contactUrl: config.contactUrl
      })),
      orderDetail: (model, actions) => mount(renderOrderDetail(document2, {
        ...model,
        config,
        actions
      })),
      profile: (model, actions) => mount(renderProfile(document2, {
        ...model,
        actions
      })),
      addresses: (model, actions) => mount(renderAddresses(document2, {
        ...model,
        actions
      })),
      addressForm: (model, actions) => mount(renderAddressForm(document2, {
        ...model,
        actions
      }))
    };
  };
  async function bootAccountPage(root, config, dependencies = {}) {
    const windowLike = dependencies.windowLike ?? window;
    const documentLike = dependencies.documentLike ?? document;
    const viewRoot = root.querySelector("[data-mypage-root]");
    if (!viewRoot) throw new Error("\u30DE\u30A4\u30DA\u30FC\u30B8\u306E\u8868\u793A\u9818\u57DF\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093\u3002");
    const auth = dependencies.auth ?? createAuthClient({
      config,
      fetchImpl: dependencies.fetchImpl ?? windowLike.fetch.bind(windowLike),
      cryptoImpl: dependencies.cryptoImpl ?? windowLike.crypto,
      storage: dependencies.storage ?? windowLike.sessionStorage,
      location: windowLike.location,
      history: windowLike.history
    });
    const controller = createAccountController({
      auth,
      apiFactory: dependencies.apiFactory ?? createCustomerAccountClient,
      routerFactory: dependencies.routerFactory ?? ((onRoute) => createRouter(windowLike, onRoute)),
      views: dependencies.views ?? createDomViews(documentLike, viewRoot, config),
      config
    });
    await controller.start();
    return controller;
  }

  // src/index.js
  var start = (scope = document) => {
    const root = scope.matches?.("[data-ageta-mypage]") ? scope : scope.querySelector?.("[data-ageta-mypage]");
    if (!root || root.dataset.agetaMypageBound === "true") return;
    root.dataset.agetaMypageBound = "true";
    try {
      const config = readPageConfig(root);
      void bootAccountPage(root, config).catch((error) => {
        if (error instanceof AuthRedirectError) return;
        root.dataset.mypageBootState = "error";
        const status = root.querySelector("[data-mypage-root]");
        if (status) status.textContent = error.message || "\u30DE\u30A4\u30DA\u30FC\u30B8\u3092\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002";
      });
    } catch (error) {
      root.dataset.mypageBootState = "configuration-error";
      const status = root.querySelector("[data-mypage-root]");
      if (status) status.textContent = error.message;
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => start(), { once: true });
  } else {
    start();
  }
  document.addEventListener("shopify:section:load", (event) => start(event.target));
})();
